import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import markerIcon from '../../../assets/icons/marker.svg';
import calendarIcon from '../../../assets/icons/calendar.svg';
import heartIcon from '../../../assets/icons/heart.svg';
import minimalIcon from '../../../assets/icons/flash.svg';
import optimalIcon from '../../../assets/icons/flash_opt.svg';
import maximalIcon from '../../../assets/icons/flash_max.svg';
import heartIconActive from '../../../assets/icons/bottomBar/active_heart.svg';

import placeholderImage from '../../../assets/images/noimage.png';

import { addToFavorites, removeFromFavorites } from "../../../api/favoritecall";
import { formatPrice } from '../../../utils/formatPrice';
import formatDate from '../../../utils/formatDate';
import { t } from 'i18next';

const measurementUnitMap = {
    "piece": "1 шт",
    "kilogram": "1 кг",
    "ton": "1 т",
    "liter": "1 л",
    "hectare": "1 га",
    "square_meter": "1 м²",
    "cubic_meter": "1 м³",
    "bag": "1 қап",
    "centner": "1 ц",
    "head": "1 бас",
    "pair": "1 жұп",
    "meter": "1 м",
    "box": "1 қорап"
};

const getUnitDisplay = (unit) => {
    if (!unit) return '';
    // Try to get translation, fallback to map
    const translation = t(unit);
    if (translation && translation !== unit) {
        return `1 ${translation}`;
    }
    return measurementUnitMap[unit] || '';
};

function VipAdvertItem(props) {
    const [isFavorite, setIsFavorite] = useState(props.is_favorite || false);
    const navigate = useNavigate();

    useEffect(() => {
        setIsFavorite(props.is_favorite || false);
    }, [props.is_favorite]);

    const handleFavoriteToggle = async (e) => {
        e.stopPropagation();

        const accessToken = localStorage.getItem("access_token");
        if (!accessToken) {
            navigate("/sign");
            return;
        }

        try {
            if (isFavorite) {
                await removeFromFavorites(props.id);
                setIsFavorite(false);
                // Call callback if provided (e.g., to remove from favorites list)
                if (props.onFavoriteToggle) {
                    props.onFavoriteToggle(props.id);
                }
            } else {
                await addToFavorites(props.id);
                setIsFavorite(true);
            }
        } catch (error) {
            console.error("Error toggling favorite:", error);
            // Revert to previous state on error
            setIsFavorite(!isFavorite);
        }
    };

    const handleClick = () => {
        const announcementUrl = `${window.location.origin}/announcement/${props.id}`;
        window.open(announcementUrl, "_blank", "noopener,noreferrer");
    };

    const getIcon = (packageType) => {
        switch (packageType) {
            case 'minimal':
                return minimalIcon;
            case 'optimal':
                return optimalIcon;
            case 'maximal':
                return maximalIcon;
            default:
                return null;
        }
    };

    const promotionIcon = getIcon(props.promotion_package);

    const truncateTitle = (text, maxLength = 50) => {
        if (text && text.length > maxLength) {
            return text.substring(0, maxLength) + '...';
        }
        return text;
    };

    return (
        <div className={`cursor-pointer bg-white rounded-[10px] flex flex-col w-full ${props.id || ''}`} onClick={handleClick}>
            <div className="relative">
                <img
                    className="cursor-pointer absolute right-5 top-5 md:right-[10px] md:top-[10px] bg-white p-[5px] rounded-full z-10"
                    src={isFavorite ? heartIconActive : heartIcon}
                    alt="like"
                    onClick={handleFavoriteToggle}
                />
                <img
                    className="w-full h-[260px] md:h-[130px] rounded-[10px] m-0 object-cover"
                    src={(props.imagePath && props.imagePath.trim() !== '') ? props.imagePath : placeholderImage}
                    alt="vip_advert_image"
                    onError={(e) => {
                        if (e.target.src !== placeholderImage) {
                            e.target.onerror = null;
                            e.target.src = placeholderImage;
                        }
                    }}
                />
                {props.is_dealer && (
                    <span className="absolute left-2 top-2 bg-[#147F26] text-white text-[10px] font-bold px-2 py-0.5 rounded-full z-10">
                        {t('dealerBadge') || 'Дилерлік'}
                    </span>
                )}
                {promotionIcon && (
                    <img className="absolute left-5 top-5 md:left-[10px] md:top-[10px] bg-white p-[5px] rounded-[20px] w-7 h-5 z-10" src={promotionIcon} alt="promotion_icon" />
                )}
                <p className="md:hidden absolute left-[55px] bottom-[15px] text-xs font-medium bg-white text-[#222222] px-[5px] py-0 rounded-[20px] m-0">{props.barter ? "Бартер" : ""}</p>
                <p className="md:hidden absolute left-[10px] bottom-[15px] text-xs font-medium bg-white text-[#222222] px-[5px] py-0 rounded-[20px] m-0">{getUnitDisplay(props.measurement_unit)}</p>
            </div>
            <div className="p-5">
                <p className="font-medium text-[#222222] text-base md:text-sm m-0 md:overflow-hidden md:text-ellipsis">{truncateTitle(props.name)}</p>
                <div className="flex items-center gap-[10px] flex-nowrap min-w-0">
                    <p className="text-[#147F26] text-base font-bold truncate min-w-0">
                        {formatPrice(props.price, props.price_type, props.is_negotiable, props.barter)}
                    </p>
                    <p className="md:block hidden text-xs font-medium border border-[#147F26] px-[5px] py-0 rounded-[20px] whitespace-nowrap flex-shrink-0">{getUnitDisplay(props.measurement_unit)}</p>
                    {props.barter && <p className="md:block hidden text-xs font-medium border border-[#147F26] px-[5px] py-0 rounded-[20px] whitespace-nowrap flex-shrink-0">Бартер</p>}
                </div>
                <div className="flex items-center flex-row">
                    <img src={markerIcon} alt="location" className="w-[19px] hidden md:flex justify-start items-center flex-col" />
                    <div className="flex flex-col">
                        <p className="text-sm font-medium text-[#222222] m-[5px] md:m-0">
                            {props.location?.province && `${props.location.province}, `}
                            {props.location?.locality || ''}
                        </p>
                    </div>
                </div>
                <div className="flex items-center flex-row">
                    <img src={calendarIcon} alt="calendar" className="w-[19px] hidden md:flex justify-start items-center flex-col" />
                    <p className="text-sm font-medium text-[#222222] m-[5px] md:m-0">
                        {formatDate(props.date)}
                    </p>
                </div>
            </div>
        </div>
    );
}

export default VipAdvertItem;